import { useEffect } from 'react';
import { accion, autoriaDe, fechaHora, VERBO_ESTADO } from '../lib/formato';
import type { Estado, Hito } from '../lib/tipos';
import { Boton, Vacio } from './ui';

/* ───────────────────── Cinta de cierre · mejora M-04 ─────────────────────
   Quién ha dejado la solicitud en su estado actual, con nombre y rol reales
   tomados del histórico. Nunca deducidos del estado.                      */

export function CintaEstado({ estado, timeline }: { estado: Estado; timeline: Hito[] }) {
  const a = autoriaDe(timeline, estado);
  const verbo = VERBO_ESTADO[estado];
  if (!a || !verbo) return null;

  const tono = estado === 'REJECTED' ? 'alto' : estado === 'CANCELLED' ? 'aviso' : 'hecho';

  return (
    <div className="cinta-cierre" data-tono={tono}>
      <span className="que">{verbo}</span>
      <span className="quien">
        {a.nombre} · {a.rolEtiqueta}
      </span>
      <span className="cuando">{fechaHora(a.fecha)}</span>
      {a.motivo && <p className="motivo">{a.motivo}</p>}
    </div>
  );
}

/* ─────────────── Panel lateral de histórico · mejora M-06 ────────────────
   «Qué ha pasado» deja de ocupar sitio permanentemente en la ficha: el
   estado ya se lee en los indicadores y en la cinta de arriba. El detalle
   completo se abre bajo demanda desde la barra de acciones.               */

export function BotonHistorial({ n, onAbrir }: { n: number; onAbrir: () => void }) {
  return (
    <Boton variante="secundario" onClick={onAbrir}>
      Qué ha pasado <span className="cuenta">{n}</span>
    </Boton>
  );
}

export function PanelHistorial({
  timeline,
  codigo,
  onCerrar,
}: {
  timeline: Hito[];
  codigo: string;
  onCerrar: () => void;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onCerrar]);

  return (
    <div className="velo" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <aside className="panel-lateral" role="dialog" aria-modal="true" aria-label="Qué ha pasado">
        <header>
          <div>
            <h2>Qué ha pasado</h2>
            <p>
              Solicitud {codigo} · {timeline.length} movimiento{timeline.length === 1 ? '' : 's'}
            </p>
          </div>
          <Boton variante="plano" onClick={onCerrar} aria-label="Cerrar el histórico">
            Cerrar
          </Boton>
        </header>

        <div className="cuerpo">
          {timeline.length === 0 ? (
            <Vacio titulo="Sin movimientos" />
          ) : (
            <ol className="linea-temporal">
              {timeline.map((h) => (
                <li
                  key={h.id}
                  data-hito={
                    ['AUTHORIZED', 'CANCELLED', 'REJECTED', 'KITCHEN_PLAN_CONFIRMED'].includes(
                      h.action,
                    )
                      ? 'true'
                      : undefined
                  }
                >
                  <div className="cuando">{fechaHora(h.createdAt)}</div>
                  <div className="que" title={h.action}>{accion(h.action)}</div>
                  <div className="sutil">{h.actorName ?? 'Sistema'}</div>
                  {h.reason && <div className="motivo">{h.reason}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}
